import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { DATE_FILTERS, FILTER_TYPES } from '../../constants';
import type {
  DateRangeErrors,
  Option,
  PurposeOfUseOption,
} from '../../interfaces/Dashboard';

interface SummaryFilterBarProps {
  filterType: Option;
  onFilterTypeChange: (value: Option) => void;
  dateFilter: Option;
  onDateFilterChange: (value: Option) => void;
  fromDate: string;
  toDate: string;
  onDateChange: (field: 'fromdate' | 'todate', value: string) => void;
  errors: DateRangeErrors;
  filterByRequestDate: boolean;
  onFilterByRequestDateChange: (value: boolean) => void;
  onClear: () => void;
  onApply: () => void;
  isLoading: boolean;
  /** Omit to hide the purpose-of-use selector (the Summary page has none). */
  purposeOfUses?: PurposeOfUseOption[];
  selectedPurposeOfUse?: PurposeOfUseOption | null;
  onPurposeOfUseChange?: (value: PurposeOfUseOption) => void;
}

/**
 * Filter bar shared by the Landing page and the Health-Systemwise Summary page.
 * From/To are only enabled for the "Custom Date" preset, matching the legacy UI.
 */
const SummaryFilterBar = ({
  filterType,
  onFilterTypeChange,
  dateFilter,
  onDateFilterChange,
  fromDate,
  toDate,
  onDateChange,
  errors,
  filterByRequestDate,
  onFilterByRequestDateChange,
  onClear,
  onApply,
  isLoading,
  purposeOfUses,
  selectedPurposeOfUse,
  onPurposeOfUseChange,
}: SummaryFilterBarProps) => {
  const isCustomRange = dateFilter.value === 'Custom';
  const hasDateError = errors.fromdate.isError || errors.todate.isError;

  return (
    <Grid container className='all-resouces-list-wrapper' spacing={3}>
      <Grid size={12}>
        <Paper className='respurce-list-paper'>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 2 }}>
              <Autocomplete
                options={FILTER_TYPES}
                value={filterType}
                size='small'
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                disableClearable
                blurOnSelect
                onChange={(_event, value) => value && onFilterTypeChange(value)}
                renderInput={(params) => (
                  <TextField {...params} label='Select Filter Type' />
                )}
                className='autocomplete-field'
              />
            </Grid>

            {purposeOfUses ? (
              <Grid size={{ xs: 12, md: 2 }}>
                <Autocomplete
                  options={purposeOfUses}
                  value={(selectedPurposeOfUse ?? null) as PurposeOfUseOption}
                  size='small'
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  disableClearable
                  blurOnSelect
                  onChange={(_event, value) =>
                    value && onPurposeOfUseChange?.(value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label='Select Purpose of use' />
                  )}
                  className='autocomplete-field'
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12, md: 2 }}>
              <Autocomplete
                options={DATE_FILTERS}
                value={dateFilter}
                size='small'
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                disableClearable
                blurOnSelect
                onChange={(_event, value) => value && onDateFilterChange(value)}
                renderInput={(params) => (
                  <TextField {...params} label='Select Date Filter' />
                )}
                className='autocomplete-field'
              />
            </Grid>

            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField
                id='fromdate'
                label='From Date'
                type='date'
                value={fromDate}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                disabled={!isCustomRange}
                onChange={(event) =>
                  onDateChange('fromdate', event.target.value)
                }
                helperText={errors.fromdate.errorMessage}
                error={errors.fromdate.isError}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField
                id='todate'
                label='To Date'
                type='date'
                value={toDate}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                disabled={!isCustomRange}
                onChange={(event) => onDateChange('todate', event.target.value)}
                helperText={errors.todate.errorMessage}
                error={errors.todate.isError}
              />
            </Grid>

            {/*
              size=3 (not the 2 every other field above uses) plus a
              nowrap override on the label: at 2/12 the "Filter by Request
              Date" checkbox label wrapped onto two lines in Firefox, where
              text metrics run a little wider than Chrome. The date fields
              had room to give up, per feedback.
            */}
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterByRequestDate}
                    onChange={(event) =>
                      onFilterByRequestDateChange(event.target.checked)
                    }
                    name='filterByRequestDate'
                    color='primary'
                  />
                }
                label='Filter by Request Date'
                sx={{
                  whiteSpace: 'nowrap',
                  '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
                }}
              />
            </Grid>

            <Grid
              size={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'baseline',
              }}
            >
              <Button
                variant='outlined'
                color='primary'
                onClick={onClear}
                className='show-req-btn'
                title='Clear Filter'
                disabled={isLoading}
              >
                Clear
              </Button>

              <Button
                variant='contained'
                color='primary'
                onClick={onApply}
                className='show-req-btn'
                title='Filter Chart'
                disabled={hasDateError || isLoading}
              >
                Filter
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SummaryFilterBar;
